import React, { useState } from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import { ActivationKey } from '../types/ActivationKey';
import Alert from 'react-bootstrap/Alert';
import { readSerialNumberFile, parseModuleKey, parseSerialNumber } from './readSerialNumberFile';
import { Unit } from '../types/Unit';
import { TableView } from './generic/tableview/TableView'
import { TableViewAction } from './generic/tableview/actions/TableViewAction';
import { FunctionAction } from './generic/tableview/actions/FunctionAction';
import { ConfirmDialog } from './generic/ConfirmDialog';
import cloneDeep from 'lodash.clonedeep';
import { ErrorAlert } from './generic/ErrorAlert';
import * as Constants from '../constants';
import { getApiDataWithPost } from '../apiFunctions/getApiDataWithPost';
import { GetLicensesResult } from '../types/GetLicensesResult';
import { License } from '../types/License';
import { OkButtonSpinner } from './generic/OkButtonSpinner';

interface SelectUnitsProps {
    setStage: (stage: "EnterActivationKey" | "SelectUnits" | "Licenses") => void,
    setLicenses: (licenses: License[]) => void,
    setLicenseXml: (licenseXml: string) => void,
    activationKey: ActivationKey | undefined,
    activationKeyCode: string
}

export const SelectUnits: React.FC<SelectUnitsProps> = props => {
    const { setStage, setLicenses, setLicenseXml, activationKey, activationKeyCode } = props;
    const [manuallyAddedHardwareId, setManuallyAddedHardwareId] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState("");
    const [units, setUnits] = useState<Unit[]>([]);
    const [showGenerateLicensesAlert, setShowGenerateLicensesAlert] = useState(false);
    const [loading, setLoading] = useState(false);

    const uploadHardwareIds = (event: any) => {
        setErrorMessage("");

        for (var i = 0; i < event.target.files.length; i++) {
            readSerialNumberFile(event.target.files[i], i === event.target.files.length - 1, setErrorMessage, units, setUnits);
        }
    }

    const addManualHardwareId = () => {
        if (!activationKey) return;

        setErrorMessage("");

        if (activationKey.useModuleKey && parseSerialNumber(manuallyAddedHardwareId) !== "") {
            setErrorMessage("You must enter a valid module key (8 digits starting with 0 or 9).");
            return;
        }

        if (!activationKey.useModuleKey && parseModuleKey(manuallyAddedHardwareId) !== "") {
            setErrorMessage("You must enter a valid serial number.");
            return;
        }

        if (units.find(u => u.moduleKey === manuallyAddedHardwareId || u.serialNumber === manuallyAddedHardwareId)) {
            setErrorMessage("That serial number or module key has already been added.");
            return;
        }

        var unitsClone = cloneDeep(units);
        unitsClone.push({
            serialNumber: parseSerialNumber(manuallyAddedHardwareId),
            moduleKey: parseModuleKey(manuallyAddedHardwareId)
        } as Unit);
        setUnits(unitsClone);

        setManuallyAddedHardwareId("");
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.charCode === 13) {
            event.preventDefault();
            addManualHardwareId();
        }
    }

    const handleGetLicensesResult = (result: GetLicensesResult) => {
        if (result.success) {
            setLicenses(result.licenses);
            setLicenseXml(atob(result.licenseXml));
            setStage("Licenses");
        } else {
            setErrorMessage(result.errorMessage);

            if (result.failedUnits) {
                var unitsClone = cloneDeep(units);
                result.failedUnits.forEach(u => {
                    var unit = unitsClone.find(unit => (unit.serialNumber !== "" && unit.serialNumber === u.serialNumber) || (unit.moduleKey !== "" && unit.moduleKey === u.moduleKey));
                    if (unit)
                        unit.licenseErrorMessage = u.failureReason;
                });
                setUnits(unitsClone);
            }
        }
    }

    const generateLicenses = () => {
        if (!activationKey) return;

        if (units.find(u => u.serialNumber.length > 30)) {
            setErrorMessage("Cannot proceed. A serial number must not contain more than 30 characters.");
            setLoading(false);
            return;
        }

        setErrorMessage("");
        getApiDataWithPost(Constants.API_GET_LICENSES_URL,
            {
                activationKeyCode: activationKeyCode,
                hardwareIds: units.map(u => { return u.moduleKey + u.serialNumber }),
                useModuleKey: activationKey.useModuleKey
            },
            setLoading, handleGetLicensesResult, setErrorMessage);
    }

    var actions = [] as TableViewAction[];
    if (setUnits)
        actions = [
            {
                text: "Remove",
                icon: "delete",
                enabled: true,
                actionFunction: (unit: Unit) => {
                    var remainingUnits;
                    if (unit.serialNumber !== "") {
                        remainingUnits = units.filter(u => u.serialNumber !== unit.serialNumber);
                    }
                    else {
                        remainingUnits = units.filter(u => u.moduleKey !== unit.moduleKey);
                    }

                    setUnits(remainingUnits);
                    setErrorMessage("");
                }
            } as FunctionAction
        ];

    return <React.Fragment>
        {errorMessage !== "" && <ErrorAlert errorMessage={errorMessage} />}
        {activationKey && units.length > activationKey.balance && <ErrorAlert errorMessage={"To many units added. You can only generate licenses for " + activationKey.balance + " units using this activation key."} />}
        {activationKey && <Form>
            <Container className="mb-3">
                <Row>
                    <Col>
                        <Alert>This activation key allows you to upgrade {activationKey.balance === 1 ? "one unit" : activationKey.balance + " units"} with <span className="fw-bold">{activationKey.description}</span></Alert>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group>
                            <h5>Enter {activationKey.useModuleKey ? "module key" : "serial number manually"}</h5>
                            <InputGroup>
                                <Form.Control
                                    name="hardwareId"
                                    id="hardwareId"
                                    type="text"
                                    value={manuallyAddedHardwareId}
                                    onChange={(e: any) => {
                                        setManuallyAddedHardwareId(e.currentTarget.value);
                                    }}
                                    onKeyPress={handleKeyPress}
                                />

                                <Button onClick={addManualHardwareId} disabled={manuallyAddedHardwareId === "" ||
                                    (activationKey.useModuleKey && (manuallyAddedHardwareId.length !== 8 || !/^[09]\d+$/.test(manuallyAddedHardwareId)))}>Add</Button>

                            </InputGroup>
                        </Form.Group>
                    </Col>
                </Row>
            </Container>
            {!activationKey.useModuleKey && <Container className="mb-3">
                <Row>
                    <Col>
                        <Form.Group>
                            <h5 className="w-100">Upload files</h5>
                            <Form.Control
                                id="fileUpload"
                                name="fileUpload"
                                type="file"
                                className="w-75"
                                onChange={uploadHardwareIds}
                                multiple={true}
                                accept=".txt, .csv, .xlsx, .xml"
                            />

                            <Form.Text>Excel spreadsheet or .txt/.csv file with one serial number per row, or WinPDM export file.</Form.Text>
                        </Form.Group>
                    </Col>
                </Row>
            </Container>}
            {units.length > 0 && <Container className="mb-3 mt-3">
                <Row>
                    <Col>
                        <TableView
                            headers={[activationKey.useModuleKey ? "Module key" : "Serial number", "Info"]}
                            data={units.map((unit, index) => {
                                return {
                                    id: index,
                                    entity: unit,
                                    cellContent: [activationKey.useModuleKey ? unit.moduleKey : unit.serialNumber, unit.licenseErrorMessage]
                                }
                            })}
                            actions={actions}
                        />
                    </Col>
                </Row>
            </Container>}
            <Container className="mb-3">
                <Row>
                    <Col>
                        <Button className="button" onClick={() => { setStage("EnterActivationKey"); }}>
                            Previous
                        </Button>
                        &nbsp;
                        <Button className="button" disabled={units.length === 0 || units.length > activationKey.balance} onClick={() => { setShowGenerateLicensesAlert(true); }}>
                            Generate licenses {loading && <OkButtonSpinner />}
                        </Button>
                    </Col>
                </Row>
            </Container>
            {showGenerateLicensesAlert && units.length <= activationKey.balance && <ConfirmDialog
                cancelFunction={() => { setShowGenerateLicensesAlert(false); }}
                content={"Generating " + units.length + " licenses will withdraw the corresponding amount from the balance of the selected activation key. Click Generate button to go ahead and do this."}
                okButtonText="Generate"
                okFunction={() => {
                    setShowGenerateLicensesAlert(false);
                    setLoading(true);
                    generateLicenses();
                }}
                show={true}
                title="Generate licenses"
            />}
        </Form>}</React.Fragment>
}